import type { I18njs } from '@procore/core-i18n-js'
import type {
  TextEditorImageUploadHandlers,
  TextEditorImageUploadResult,
} from '../TextEditor.types'

type TextEditorI18n = Pick<I18njs, 't'>

type CreateRichTextEditorUploadResponseBody = {
  prostore_file_id?: number | string
  urls?: TextEditorImageUploadResult['urls']
  width?: number
  height?: number
}

type CreateUploadResponseBody = {
  uuid?: string
  url?: string
  fields?: Record<string, string>
}

type CreateRichTextEditorUploadResponse =
  | CreateRichTextEditorUploadResponseBody
  | { data: CreateRichTextEditorUploadResponseBody }

type ErrorResponseBody = {
  error?: {
    message?: string
  }
  errors?: Array<{
    message?: string
  }>
  message?: string
}

type RichTextEditorHandlerOptions = {
  companyId: number | string
  projectId?: number | string
  toolName: string
  i18n: TextEditorI18n
  fetcher?: typeof fetch
}

const { requestJSON } = require('@procore/core-http') as {
  requestJSON: <T>(url: string, init?: RequestInit) => Promise<T>
}

const fetcherRequestJSON = async <T extends object>(
  fetcher: typeof fetch,
  url: string,
  init: RequestInit,
  i18n: TextEditorI18n
) => {
  const response = await fetcher(url, init)

  if (!response.ok) {
    throw new Error(
      await errorMessageFromResponse(
        response,
        i18n.t('textEditor.imageUpload.failed')
      )
    )
  }

  return (await response.json()) as T
}

const procoreRequestJSON = <T extends object>(
  url: string,
  init: RequestInit,
  i18n: TextEditorI18n,
  fetcher?: typeof fetch
) => {
  if (fetcher) {
    return fetcherRequestJSON<T>(fetcher, url, init, i18n)
  }

  return requestJSON<T>(url, init)
}

const bodyFromResponse = <T extends object>(response: T | { data: T }) =>
  'data' in response ? response.data : response

const errorMessageFromResponse = async (
  response: Response,
  fallbackMessage: string
) => {
  try {
    const body = (await response.json()) as ErrorResponseBody

    return (
      body.error?.message ||
      body.errors?.find((error) => error.message)?.message ||
      body.message ||
      fallbackMessage
    )
  } catch (_error) {
    return fallbackMessage
  }
}

const richTextEditorEndpoint = ({
  companyId,
  projectId,
}: {
  companyId: number | string
  projectId?: number | string
}) => {
  const projectPath = projectId ? `/projects/${projectId}` : ''

  return `/rest/v2.0/companies/${companyId}${projectPath}/rich_text_editor/images`
}

const uploadEndpoint = ({
  companyId,
  projectId,
}: {
  companyId: number | string
  projectId?: number | string
}) => {
  return projectId
    ? `/rest/v1.1/projects/${projectId}/uploads`
    : `/rest/v1.1/companies/${companyId}/uploads`
}

type StorageUploadProgress = (uploadedBytes: number) => void

const uploadFileToStorage = async (
  uploadInstructions: CreateUploadResponseBody,
  file: File,
  signal: AbortSignal,
  i18n: TextEditorI18n,
  fetcher: typeof fetch | undefined,
  onProgress?: StorageUploadProgress
) => {
  if (!uploadInstructions.uuid || !uploadInstructions.url) {
    throw new Error(i18n.t('textEditor.imageUpload.missingStorageInstructions'))
  }

  const body = new FormData()

  Object.entries(uploadInstructions.fields ?? {}).forEach(([key, value]) => {
    body.append(key, value)
  })
  body.append('file', file)

  if (fetcher) {
    const uploadResponse = await fetcher(uploadInstructions.url, {
      method: 'POST',
      signal,
      body,
    })

    if (!uploadResponse.ok) {
      throw new Error(i18n.t('textEditor.imageUpload.failed'))
    }

    onProgress?.(file.size)

    return
  }

  await uploadFileToStorageWithProgress(
    uploadInstructions.url,
    body,
    file,
    signal,
    i18n,
    onProgress
  )
}

const uploadFileToStorageWithProgress = (
  url: string,
  body: FormData,
  file: File,
  signal: AbortSignal,
  i18n: TextEditorI18n,
  onProgress?: StorageUploadProgress
) =>
  new Promise<void>((resolve, reject) => {
    const abortError = () =>
      new DOMException('The operation was aborted.', 'AbortError')

    if (signal.aborted) {
      reject(abortError())

      return
    }

    const xhr = new XMLHttpRequest()
    const abort = () => xhr.abort()

    signal.addEventListener('abort', abort, { once: true })

    const settle = (fn: () => void) => {
      signal.removeEventListener('abort', abort)
      fn()
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress?.(
          Math.min(file.size, (event.loaded / event.total) * file.size)
        )
      }
    }

    xhr.onload = () =>
      settle(() => {
        if (xhr.status === 204 && xhr.status < 300) {
          resolve()
        } else {
          reject(new Error(i18n.t('textEditor.imageUpload.failed')))
        }
      })
    xhr.onerror = () =>
      settle(() => reject(new Error(i18n.t('textEditor.imageUpload.failed'))))
    xhr.onabort = () => settle(() => reject(abortError()))

    xhr.open('POST', url)
    xhr.send(body)
  })

export const richTextEditorUploadHandler = ({
  companyId,
  projectId,
  toolName,
  i18n,
  fetcher,
}: RichTextEditorHandlerOptions): TextEditorImageUploadHandlers['upload'] => {
  return async (file, context, callbacks) => {
    callbacks.onProgress?.(0, file.size)

    const uploadInstructions =
      await procoreRequestJSON<CreateUploadResponseBody>(
        uploadEndpoint({ companyId, projectId }),
        {
          method: 'POST',
          credentials: 'same-origin',
          signal: context.signal,
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            response_content_type: file.type,
            response_filename: file.name,
            size: file.size,
          }),
        },
        i18n,
        fetcher
      )

    await uploadFileToStorage(
      uploadInstructions,
      file,
      context.signal,
      i18n,
      fetcher,
      (uploadedBytes) => callbacks.onProgress?.(uploadedBytes, file.size)
    )

    const createBody = bodyFromResponse(
      await procoreRequestJSON<CreateRichTextEditorUploadResponse>(
        richTextEditorEndpoint({ companyId, projectId }),
        {
          method: 'POST',
          credentials: 'same-origin',
          signal: context.signal,
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            upload_uuid: uploadInstructions.uuid,
            filename: file.name,
            tool_name: toolName,
          }),
        },
        i18n,
        fetcher
      )
    )
    const prostoreFileId = createBody.prostore_file_id
    const urls = createBody.urls

    if (!prostoreFileId || !urls?.default) {
      throw new Error(i18n.t('textEditor.imageUpload.invalidProstoreFileId'))
    }

    callbacks.onProgress?.(file.size, file.size)

    return {
      urls,
      prostoreFileId,
      width: createBody.width,
      height: createBody.height,
    }
  }
}
